import React, { useState, useEffect } from 'react';
import { Moon, Sun, Menu, X, ChevronDown, ChevronUp, Gamepad2, Share2, Upload } from 'lucide-react'; // Added Upload icon

// Define theme objects
const darkTheme = {
    bodyBackground: '#1a202c',
    bodyColor: '#f7fafc',
    sidebarBackground: '#1a202c',
    sidebarColor: '#f7fafc',
    sidebarBorder: '#4a5568',
    headerBackground: '#1a202c',
    headerColor: '#fff',
    navItemColor: '#f7fafc',
    navItemHoverBackground: '#2d3748',
    iconColor: '#cbd5e0',
    toggleBackground: '#374151',
    toggleColor: '#fff',
    logoutButtonBackground: '#374151',
    logoutButtonColor: '#f7fafc',
    logoutButtonHoverBackground: '#2d3748',
    dropdownBackground: '#1a202c',
    dropdownItemHoverBackground: '#374151',
};

const lightTheme = {
    bodyBackground: '#f7fafc',
    bodyColor: '#2d3748',
    sidebarBackground: '#f7fafc',
    sidebarColor: '#2d3748',
    sidebarBorder: '#e2e8f0',
    headerBackground: '#f7fafc',
    headerColor: '#2d3748',
    navItemColor: '#2d3748',
    navItemHoverBackground: '#edf2f7',
    iconColor: '#718096',
    toggleBackground: '#e2e8f0',
    toggleColor: '#4a5568',
    logoutButtonBackground: '#e2e8f0',
    logoutButtonColor: '#2d3748',
    logoutButtonHoverBackground: '#cbd5e0',
    dropdownBackground: '#f7fafc',
    dropdownItemHoverBackground: '#e2e8f0',
};

const AdminPanel = () => {
    // Dummy user data - replace with your actual user data fetching logic
    const userName = 'John Doe';
    const userRole = 'Administrator';
    const companyName = 'The Concession Shop'; // Company name

    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false); // Default to open on desktop
    const [isMobileNavVisible, setIsMobileNavVisible] = useState(false); // Default to hidden on mobile
    const [isDarkMode, setIsDarkMode] = useState(true); // State for dark/light mode
    const [isGamesDropdownOpen, setIsGamesDropdownOpen] = useState(false); // State for the Games dropdown
    const [isSocialMarketDropdownOpen, setIsSocialMarketDropdownOpen] = useState(false); // State for the Social Market dropdown

    // Determine current theme based on isDarkMode
    const currentTheme = isDarkMode ? darkTheme : lightTheme;

    // Apply theme to the body and localStorage
    useEffect(() => {
        document.body.style.backgroundColor = currentTheme.bodyBackground;
        document.body.style.color = currentTheme.bodyColor;
        localStorage.setItem('theme', isDarkMode ? 'dark' : 'light'); // Save theme
    }, [isDarkMode, currentTheme]);

    // Load theme from localStorage
    useEffect(() => {
        const savedTheme = localStorage.getItem('theme');
        if (savedTheme) {
            setIsDarkMode(savedTheme === 'dark');
        }
    }, []);

    // Close mobile nav and dropdown on window resize (desktop breakpoint)
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth > 768 && isMobileNavVisible) {
                setIsMobileNavVisible(false);
                setIsGamesDropdownOpen(false);
                setIsSocialMarketDropdownOpen(false); // Also close dropdown on desktop resize
            }
            // Optional: Adjust initial collapsed state based on window size on mount
            // if (window.innerWidth <= 768 && !isSidebarCollapsed) {
            //     setIsSidebarCollapsed(true);
            // } else if (window.innerWidth > 768 && isSidebarCollapsed) {
            //     setIsSidebarCollapsed(false); // Or keep it collapsed if that's the preference
            // }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [isMobileNavVisible, isSidebarCollapsed]);


    const baseStyle = {
        backgroundColor: currentTheme.sidebarBackground,
        color: currentTheme.sidebarColor,
        paddingTop: '20px',
        paddingLeft: '10px',
        paddingRight: '10px',
        transition: 'all 0.3s ease-in-out',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        zIndex: 10,
        overflowX: 'hidden',
        alignItems: 'center',
    };

    const sidebarDesktopStyle = {
        ...baseStyle,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        position: 'fixed',
        top: 0,
        left: 0,
        width: isSidebarCollapsed ? '64px' : '250px',
        borderRight: `1px solid ${currentTheme.sidebarBorder}`,
    };

    const sidebarMobileStyle = {
        ...baseStyle,
        display: 'flex',
        flexDirection: 'column',
        position: 'fixed',
        top: 0,
        left: 0,
        bottom: 0,
        width: '250px',
        paddingTop: '20px',
        transform: isMobileNavVisible ? 'translateX(0)' : 'translateX(-100%)',
        transition: 'transform 0.3s ease-in-out',
        borderRight: `1px solid ${currentTheme.sidebarBorder}`,
    };

    const headerDesktopStyle = {
        padding: '10px',
        textAlign: 'center',
        borderBottom: `1px solid ${currentTheme.sidebarBorder}`,
        marginBottom: '20px',
        color: currentTheme.headerColor,
        width: '100%',
    };

    const logoDesktopStyle = {
        fontSize: '1.5rem',
        fontWeight: 'bold',
        marginBottom: '5px',
        transition: 'transform 0.3s ease',
        transform: isSidebarCollapsed ? 'rotate(180deg)' : 'rotate(0deg)',
        display: 'inline-block',
        color: currentTheme.headerColor,
    };

    const userInfoDesktopStyle = {
        fontSize: '0.9rem',
        color: currentTheme.iconColor,
    };

    const navDesktopStyle = {
        listStyle: 'none',
        padding: 0,
        margin: 0,
        width: '100%',
    };

    const navMobileStyle = {
        listStyle: 'none',
        padding: 0,
        margin: 0,
        width: '100%',
    };

    const navItemBaseStyle = {
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
        transition: 'background-color 0.2s ease',
        borderRadius: '6px',
        textDecoration: 'none',
        color: currentTheme.navItemColor,
        padding: '10px',
        marginBottom: '4px',
        // Ensure text doesn't wrap icons when collapsed
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    };


    const navItemStyleHover = {
        backgroundColor: currentTheme.navItemHoverBackground,
    };

    const navItemTextDesktopStyle = {
        marginLeft: '10px',
        fontWeight: '500',
        display: isSidebarCollapsed ? 'none' : 'inline',
    };

    const iconBaseStyle = {
        color: currentTheme.iconColor,
        width: '20px',
        height: '20px',
        flexShrink: 0, // Prevent icon from shrinking
    };

    const iconDesktopStyle = {
        ...iconBaseStyle,
        marginRight: isSidebarCollapsed ? '0px' : '10px',
    };

    const toggleButtonBaseStyle = {
        cursor: 'pointer',
        padding: '6px',
        borderRadius: '6px',
        backgroundColor: currentTheme.toggleBackground,
        transition: 'background-color 0.2s ease',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '30px',
        height: '30px',
    };

    const toggleButtonDesktopStyle = {
        ...toggleButtonBaseStyle,
        position: 'absolute',
        top: '20px',
        left: isSidebarCollapsed ? '10px' : '210px',
        transform: 'translateX(0)',
        zIndex: 11,
        flexDirection: 'column',
        alignItems: 'center',
    };

    const toggleButtonMobileStyle = {
        ...toggleButtonBaseStyle,
        position: 'fixed',
        top: '20px',
        left: '20px',
        zIndex: 11,
    };

    const themeSwitchStyle = {
        ...toggleButtonBaseStyle,
        position: 'absolute',
        bottom: '20px',
        left: '50%',
        transform: 'translateX(-50%)',
        cursor: 'pointer',
    };

    const themeIconStyle = {
        width: '18px',
        height: '18px',
        color: currentTheme.toggleColor,
    };

    const toggleIconStyle = {
        color: currentTheme.toggleColor,
        width: '18px',
        height: '18px',
        transition: 'transform 0.3s ease',
        marginBottom: '2px',
    };

    const toggleTextDesktopStyle = {
        fontSize: '0.7rem',
        color: currentTheme.iconColor,
        display: isSidebarCollapsed ? 'none' : 'block',
    };

    const logoutButtonStyle = {
        ...navItemBaseStyle,
        textAlign: 'left',
        backgroundColor: currentTheme.logoutButtonBackground,
        color: currentTheme.logoutButtonColor,
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        borderTop: `1px solid ${currentTheme.sidebarBorder}`,
        position: 'absolute',
        bottom: '80px',
        left: '0',
        width: '100%',
    };

    const logoutButtonStyleHover = {
        backgroundColor: currentTheme.logoutButtonHoverBackground,
    };

    const toggleDesktopSidebar = () => {
        setIsSidebarCollapsed(!isSidebarCollapsed);
        // Close dropdown when collapsing sidebar
        if (!isSidebarCollapsed) {
            setIsGamesDropdownOpen(false);
            setIsSocialMarketDropdownOpen(false);
        }
    };

    const toggleMobileNav = () => {
        setIsMobileNavVisible(!isMobileNavVisible);
        // Close dropdown when hiding mobile nav
        if (!isMobileNavVisible) {
            setIsGamesDropdownOpen(false);
            setIsSocialMarketDropdownOpen(false);
        }
    };

    const handleThemeSwitch = () => {
        setIsDarkMode(!isDarkMode);
    };

    const handleLogout = () => {
        console.log('Logging out...');
        // window.location.href = '/login'; // Example redirection
    };

    const toggleGamesDropdown = () => {
        setIsGamesDropdownOpen(!isGamesDropdownOpen);
    };

    const handleGameSelect = () => {
        // Close mobile nav and dropdown when a game is selected in mobile view
        if (isMobile) {
            setIsMobileNavVisible(false);
        }
        setIsGamesDropdownOpen(false);
        // Add navigation logic here based on which game was selected
        console.log("Game selected, dropdown closed.");
    };

    const toggleSocialMarketDropdown = () => {
        setIsSocialMarketDropdownOpen(!isSocialMarketDropdownOpen);
    };

    const handleSocialMarketSelect = (option) => {
        // Close mobile nav and dropdown when an option is selected in mobile view
        if (isMobile) {
            setIsMobileNavVisible(false);
        }
        setIsSocialMarketDropdownOpen(false);
        // Add navigation logic here based on which option was selected
        console.log(`Social Market option selected: ${option}`);
        // You can implement actions based on the selected option here, e.g., open a new tab with the social media link
        switch (option) {
            case 'Facebook':
                window.open('https://www.facebook.com', '_blank');
                break;
            case 'Instagram':
                window.open('https://www.instagram.com', '_blank');
                break;
            case 'LinkedIn':
                window.open('https://www.linkedin.com', '_blank');
                break;
            case 'YouTube':
                window.open('https://www.youtube.com', '_blank');
                break;
            case 'WhatsApp':
                window.open('https://web.whatsapp.com/', '_blank'); // Or use a specific link if needed
                break;
            case 'E-mail':
                window.location.href = 'mailto:your-email@example.com'; // Replace with your desired email address or logic
                break;
            default:
                break;
        }
    };


    const isMobile = window.innerWidth <= 768; // Use window.innerWidth directly

    // Define navItemDesktopStyle and other dynamic styles here
    const navItemDesktopStyle = {
        ...navItemBaseStyle,
        padding: '10px',
        marginBottom: '4px',
    };

    const dropdownContainerStyle = {
        listStyle: 'none',
        padding: '0 0 0 20px', // Indent dropdown items
        margin: '0',
        transition: 'height 0.3s ease-in-out, opacity 0.3s ease-in-out',
        overflow: 'hidden', // Hide items when collapsed
        height: 'auto', // Let CSS handle height based on isGamesDropdownOpen and isSocialMarketDropdownOpen
        opacity: 1, // Let CSS handle opacity
        pointerEvents: 'auto', // Let CSS handle pointer events
    };

    const dropdownItemStyle = {
        ...navItemBaseStyle, // Reuse base item styles
        padding: '8px 10px', // Slightly smaller padding than parent
        marginBottom: '2px', // Less margin between dropdown items
        fontSize: '0.95em', // Slightly smaller font size
        backgroundColor: 'transparent', // Default transparent background
    };

    const dropdownItemStyleHover = {
        backgroundColor: currentTheme.dropdownItemHoverBackground, // Different hover color
    };


    return (
        <div className="flex">
            {/* Mobile Sidebar Toggle Button */}
            {isMobile && (
                <div
                    style={toggleButtonMobileStyle}
                    onClick={toggleMobileNav}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = currentTheme.toggleColor)}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = currentTheme.toggleBackground)}
                >
                    {isMobileNavVisible ? <X style={toggleIconStyle} /> : <Menu style={toggleIconStyle} />}
                </div>
            )}

            {/* Sidebar */}
            {isMobile ? (
                <div style={sidebarMobileStyle}>
                    <div style={{ padding: '10px', textAlign: 'center', marginBottom: '20px', borderBottom: `1px solid ${currentTheme.sidebarBorder}` }}>
                        <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: currentTheme.headerColor }}>
                            {companyName}
                        </div>
                        <div style={{ fontSize: '0.9rem', color: currentTheme.iconColor }}>
                            {userName} ({userRole})
                        </div>
                    </div>
                    <nav style={{ width: '100%', overflowY: 'auto' }}> {/* Added overflowY for long menus */}
                        <ul style={navMobileStyle}>
                            <li>
                                <a href="/dashboard" style={{ ...navItemBaseStyle }} onClick={() => setIsMobileNavVisible(false)}
                                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = currentTheme.navItemHoverBackground)}
                                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                                >
                                    <span style={iconBaseStyle}>🛚</span>
                                    <span>Dashboard</span>
                                </a>
                            </li>
                            <li>
                                <a href="/ad" style={{ ...navItemBaseStyle }} onClick={() => setIsMobileNavVisible(false)}
                                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = currentTheme.navItemHoverBackground)}
                                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                                >
                                    <span style={iconBaseStyle}>➕</span>
                                    <span>Add Product</span>
                                </a>
                            </li>
                            {/* Add Upload Post for Mobile */}
                            <li>
                                <a href="/upload-post" style={{ ...navItemBaseStyle }} onClick={() => setIsMobileNavVisible(false)}
                                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = currentTheme.navItemHoverBackground)}
                                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                                >
                                    <span style={iconBaseStyle}><Upload /></span>
                                    <span>Add Upload Post</span>
                                </a>
                            </li>
                            <li>
                                <a href="/om" style={{ ...navItemBaseStyle }} onClick={() => setIsMobileNavVisible(false)}
                                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = currentTheme.navItemHoverBackground)}
                                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                                >
                                    <span style={iconBaseStyle}>📋</span>
                                    <span>Orders</span>
                                </a>
                            </li>
                            <li>
                                <a href="/cm" style={{ ...navItemBaseStyle }} onClick={() => setIsMobileNavVisible(false)}
                                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = currentTheme.navItemHoverBackground)}
                                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                                >
                                    <span style={iconBaseStyle}>➕</span>
                                    <span>Add Category</span>
                                </a>
                            </li>
                            <li>
                                <a href="/users" style={{ ...navItemBaseStyle }} onClick={() => setIsMobileNavVisible(false)}
                                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = currentTheme.navItemHoverBackground)}
                                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                                >
                                    <span style={iconBaseStyle}>👤</span>
                                    <span>Users</span>
                                </a>
                            </li>

                            {/* Games Dropdown Parent - Mobile */}
                            <li>
                                <div
                                    style={{ ...navItemBaseStyle, justifyContent: 'space-between' }}
                                    onClick={toggleGamesDropdown}
                                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = currentTheme.navItemHoverBackground)}
                                    onMouseLeave={(e) => {
                                        // Keep highlighted if dropdown is open
                                        if (!isGamesDropdownOpen) {
                                            e.currentTarget.style.backgroundColor = 'transparent';
                                        }
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <Gamepad2 style={iconBaseStyle} /> {/* Games Icon */}
                                        <span>Games</span>
                                    </div>
                                    {isGamesDropdownOpen ? <ChevronUp style={iconBaseStyle} /> : <ChevronDown style={iconBaseStyle} />}
                                </div>
                                {/* Games Dropdown List - Mobile */}
                                <ul style={{ ...dropdownContainerStyle, height: isGamesDropdownOpen ? 'auto' : '0', opacity: isGamesDropdownOpen ? 1 : 0, pointerEvents: isGamesDropdownOpen ? 'auto' : 'none' }}>
                                    <li>
                                        <a href="/games/chess" style={dropdownItemStyle} onClick={handleGameSelect}
                                            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = dropdownItemStyleHover.backgroundColor)}
                                            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                                        >
                                            Chess
                                        </a>
                                    </li>
                                    <li>
                                        <a href="/games/ludo" style={dropdownItemStyle} onClick={handleGameSelect}
                                            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = dropdownItemStyleHover.backgroundColor)}
                                            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                                        >
                                            Ludo
                                        </a>
                                    </li>
                                    <li>
                                        <a href="/games/snake-ladder" style={dropdownItemStyle} onClick={handleGameSelect}
                                            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = dropdownItemStyleHover.backgroundColor)}
                                            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                                        >
                                            Snake and Ladder
                                        </a>
                                    </li>
                                </ul>
                            </li>

                            {/* Social Market Dropdown Parent - Mobile */}
                            <li>
                                <div
                                    style={{ ...navItemBaseStyle, justifyContent: 'space-between' }}
                                    onClick={toggleSocialMarketDropdown}
                                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = currentTheme.navItemHoverBackground)}
                                    onMouseLeave={(e) => {
                                        // Keep highlighted if dropdown is open
                                        if (!isSocialMarketDropdownOpen) {
                                            e.currentTarget.style.backgroundColor = 'transparent';
                                        }
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <Share2 style={iconBaseStyle} /> {/* Social Market Icon */}
                                        <span>Social Market</span>
                                    </div>
                                    {isSocialMarketDropdownOpen ? <ChevronUp style={iconBaseStyle} /> : <ChevronDown style={iconBaseStyle} />}
                                </div>
                                {/* Social Market Dropdown List - Mobile */}
                                <ul style={{ ...dropdownContainerStyle, height: isSocialMarketDropdownOpen ? 'auto' : '0', opacity: isSocialMarketDropdownOpen ? 1 : 0, pointerEvents: isSocialMarketDropdownOpen ? 'auto' : 'none' }}>
                                    <li>
                                        <a href="#" style={dropdownItemStyle} onClick={() => handleSocialMarketSelect('Facebook')}
                                            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = dropdownItemStyleHover.backgroundColor)}
                                            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                                        >
                                            Facebook
                                        </a>
                                    </li>
                                    <li>
                                        <a href="#" style={dropdownItemStyle} onClick={() => handleSocialMarketSelect('Instagram')}
                                            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = dropdownItemStyleHover.backgroundColor)}
                                            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                                        >
                                            Instagram
                                        </a>
                                    </li>
                                    <li>
                                        <a href="#" style={dropdownItemStyle} onClick={() => handleSocialMarketSelect('LinkedIn')}
                                            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = dropdownItemStyleHover.backgroundColor)}
                                            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                                        >
                                            LinkedIn
                                        </a>
                                    </li>
                                    <li>
                                        <a href="#" style={dropdownItemStyle} onClick={() => handleSocialMarketSelect('YouTube')}
                                            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = dropdownItemStyleHover.backgroundColor)}
                                            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                                        >
                                            YouTube
                                        </a>
                                    </li>
                                    <li>
                                        <a href="#" style={dropdownItemStyle} onClick={() => handleSocialMarketSelect('WhatsApp')}
                                            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = dropdownItemStyleHover.backgroundColor)}
                                            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                                        >
                                            WhatsApp
                                        </a>
                                    </li>
                                    <li>
                                        <a href="#" style={dropdownItemStyle} onClick={() => handleSocialMarketSelect('E-mail')}
                                            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = dropdownItemStyleHover.backgroundColor)}
                                            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                                        >
                                            E-mail
                                        </a>
                                    </li>
                                </ul>
                            </li>
                        </ul>
                    </nav>
                    <div
                        style={{ ...logoutButtonStyle, textAlign: 'center' }}
                        onClick={() => { handleLogout(); setIsMobileNavVisible(false); }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = currentTheme.logoutButtonHoverBackground}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = currentTheme.logoutButtonBackground}
                    >
                        Logout
                    </div>
                    <div
                        style={themeSwitchStyle}
                        onClick={handleThemeSwitch}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = currentTheme.toggleColor)}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = currentTheme.toggleBackground)}
                    >
                        {isDarkMode ? <Sun style={themeIconStyle} /> : <Moon style={themeIconStyle} />}
                    </div>
                </div>
            ) : (
                <div style={sidebarDesktopStyle}>
                    {/* Desktop Sidebar Toggle Button with Company Name */}
                    <div
                        style={toggleButtonDesktopStyle}
                        onClick={toggleDesktopSidebar}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = currentTheme.toggleColor)}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = currentTheme.toggleBackground)}
                    >
                        {isSidebarCollapsed ? <Menu style={toggleIconStyle} /> : <X style={toggleIconStyle} />}
                        {/* Show full company name only when not collapsed */}
                        <span style={toggleTextDesktopStyle}>{companyName}</span>
                    </div>


                    <div style={headerDesktopStyle}>
                        {!isSidebarCollapsed && (
                            <div style={logoDesktopStyle}>
                                {/* No Company Name here anymore, moved to toggle button */}
                            </div>
                        )}
                        {!isSidebarCollapsed && (
                            <div style={userInfoDesktopStyle}>
                                {userName} ({userRole})
                            </div>
                        )}
                    </div>
                    <nav style={{ width: '100%', overflowY: 'auto' }}> {/* Added overflowY */}
                        <ul style={navDesktopStyle}>
                            <li>
                                <a
                                    href="/dashboard"
                                    style={{ ...navItemBaseStyle, ...navItemDesktopStyle }}
                                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = currentTheme.navItemHoverBackground)}
                                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                                >
                                    <span style={iconDesktopStyle}>🛚</span>
                                    <span style={navItemTextDesktopStyle}>Dashboard</span>
                                </a>
                            </li>
                            <li>
                                <a
                                    href="/ad"
                                    style={{ ...navItemBaseStyle, ...navItemDesktopStyle }}
                                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = currentTheme.navItemHoverBackground)}
                                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                                >
                                    <span style={iconDesktopStyle}>➕</span>
                                    <span style={navItemTextDesktopStyle}>Add Product</span>
                                </a>
                            </li>
                            {/* Add Upload Post for Desktop */}
                            <li>
                                <a
                                    href="/upload-post"
                                    style={{ ...navItemBaseStyle, ...navItemDesktopStyle }}
                                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = currentTheme.navItemHoverBackground)}
                                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                                >
                                    <span style={iconDesktopStyle}><Upload /></span>
                                    <span style={navItemTextDesktopStyle}>Add Upload Post</span>
                                </a>
                            </li>
                            <li>
                                <a
                                    href="/om"
                                    style={{ ...navItemBaseStyle, ...navItemDesktopStyle }}
                                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = currentTheme.navItemHoverBackground)}
                                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                                >
                                    <span style={iconDesktopStyle}>📋</span>
                                    <span style={navItemTextDesktopStyle}>See Orders</span>
                                </a>
                            </li>
                            <li>
                                <a
                                    href="/cm"
                                    style={{ ...navItemBaseStyle, ...navItemDesktopStyle }}
                                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = currentTheme.navItemHoverBackground)}
                                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                                >
                                    <span style={iconDesktopStyle}>➕</span>
                                    <span style={navItemTextDesktopStyle}>Add Category</span>
                                </a>
                            </li>
                            <li>
                                <a
                                    href="/users"
                                    style={{ ...navItemBaseStyle, ...navItemDesktopStyle }}
                                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = currentTheme.navItemHoverBackground)}
                                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                                >
                                    <span style={iconDesktopStyle}>👤</span>
                                    <span style={navItemTextDesktopStyle}>See Users</span>
                                </a>
                            </li>

                            {/* Games Dropdown Parent - Desktop */}
                            <li>
                                <div
                                    style={{ ...navItemBaseStyle, ...navItemDesktopStyle, justifyContent: 'space-between' }}
                                    onClick={toggleGamesDropdown}
                                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = currentTheme.navItemHoverBackground)}
                                    onMouseLeave={(e) => {
                                        // Keep highlighted if dropdown is open AND sidebar is not collapsed
                                        if (!isGamesDropdownOpen || isSidebarCollapsed) {
                                            e.currentTarget.style.backgroundColor = 'transparent';
                                        }
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <Gamepad2 style={iconDesktopStyle} /> {/* Games Icon */}
                                        <span style={navItemTextDesktopStyle}>Games</span>
                                    </div>
                                    {!isSidebarCollapsed && (isGamesDropdownOpen ? <ChevronUp style={iconBaseStyle} /> : <ChevronDown style={iconBaseStyle} />)}
                                </div>
                                {/* Games Dropdown List - Desktop */}
                                {!isSidebarCollapsed && ( // Only show dropdown list when sidebar is not collapsed
                                    <ul style={{ ...dropdownContainerStyle, height: isGamesDropdownOpen ? 'auto' : '0', opacity: isGamesDropdownOpen ? 1 : 0, pointerEvents: isGamesDropdownOpen ? 'auto' : 'none' }}>
                                        <li>
                                            <a href="/chess" style={dropdownItemStyle} onClick={handleGameSelect}
                                                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = dropdownItemStyleHover.backgroundColor)}
                                                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                                            >
                                                Chess
                                            </a>
                                        </li>
                                        <li>
                                            <a href="/ludo" style={dropdownItemStyle} onClick={handleGameSelect}
                                                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = dropdownItemStyleHover.backgroundColor)}
                                                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                                            >
                                                Ludo
                                            </a>
                                        </li>
                                        <li>
                                            <a href="/games/snake-ladder" style={dropdownItemStyle} onClick={handleGameSelect}
                                                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = dropdownItemStyleHover.backgroundColor)}
                                                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                                            >
                                                Snake and Ladder
                                            </a>
                                        </li>
                                    </ul>
                                )}
                            </li>

                            {/* Social Market Dropdown Parent - Desktop */}
                            <li>
                                <div
                                    style={{ ...navItemBaseStyle, ...navItemDesktopStyle, justifyContent: 'space-between' }}
                                    onClick={toggleSocialMarketDropdown}
                                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = currentTheme.navItemHoverBackground)}
                                    onMouseLeave={(e) => {
                                        // Keep highlighted if dropdown is open AND sidebar is not collapsed
                                        if (!isSocialMarketDropdownOpen || isSidebarCollapsed) {
                                            e.currentTarget.style.backgroundColor = 'transparent';
                                        }
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <Share2 style={iconDesktopStyle} /> {/* Social Market Icon */}
                                        <span style={navItemTextDesktopStyle}>Social Market</span>
                                    </div>
                                    {!isSidebarCollapsed && (isSocialMarketDropdownOpen ? <ChevronUp style={iconBaseStyle} /> : <ChevronDown style={iconBaseStyle} />)}
                                </div>
                                {/* Social Market Dropdown List - Desktop */}
                                {!isSidebarCollapsed && ( // Only show dropdown list when sidebar is not collapsed
                                    <ul style={{ ...dropdownContainerStyle, height: isSocialMarketDropdownOpen ? 'auto' : '0', opacity: isSocialMarketDropdownOpen ? 1 : 0, pointerEvents: isSocialMarketDropdownOpen ? 'auto' : 'none' }}>
                                        <li>
                                            <a href="#" style={dropdownItemStyle} onClick={() => handleSocialMarketSelect('Facebook')}
                                                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = dropdownItemStyleHover.backgroundColor)}
                                                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                                            >
                                                Facebook
                                            </a>
                                        </li>
                                        <li>
                                            <a href="#" style={dropdownItemStyle} onClick={() => handleSocialMarketSelect('Instagram')}
                                                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = dropdownItemStyleHover.backgroundColor)}
                                                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                                            >
                                                Instagram
                                            </a>
                                        </li>
                                        <li>
                                            <a href="#" style={dropdownItemStyle} onClick={() => handleSocialMarketSelect('LinkedIn')}
                                                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = dropdownItemStyleHover.backgroundColor)}
                                                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                                            >
                                                LinkedIn
                                            </a>
                                        </li>
                                        <li>
                                            <a href="#" style={dropdownItemStyle} onClick={() => handleSocialMarketSelect('YouTube')}
                                                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = dropdownItemStyleHover.backgroundColor)}
                                                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                                            >
                                                YouTube
                                            </a>
                                        </li>
                                        <li>
                                            <a href="#" style={dropdownItemStyle} onClick={() => handleSocialMarketSelect('WhatsApp')}
                                                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = dropdownItemStyleHover.backgroundColor)}
                                                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                                            >
                                                WhatsApp
                                            </a>
                                        </li>
                                        <li>
                                            <a href="#" style={dropdownItemStyle} onClick={() => handleSocialMarketSelect('E-mail')}
                                                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = dropdownItemStyleHover.backgroundColor)}
                                                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                                            >
                                                E-mail
                                            </a>
                                        </li>
                                    </ul>
                                )}
                            </li>

                        </ul></nav>
                    <div
                        style={themeSwitchStyle}
                        onClick={handleThemeSwitch}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = currentTheme.toggleColor)}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = currentTheme.toggleBackground)}
                    >
                        {isDarkMode ? <Sun style={themeIconStyle} /> : <Moon style={themeIconStyle} />}
                    </div>
                    <div
                        style={{ ...logoutButtonStyle }}
                        onClick={handleLogout}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = currentTheme.logoutButtonHoverBackground}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = currentTheme.logoutButtonBackground}
                    >
                        {!isSidebarCollapsed && 'Logout'}
                    </div>
                </div>
            )}

        </div>
    );
};

export default AdminPanel;